import React, { useEffect, useRef } from "react";
import { getDockPositionStyles } from "../utils/utils";
import { getThemeDesign } from "../theme/theme.manager";

/**
 * チャットパネルコンポーネントのプロパティ
 */
interface ChatPanelProps {
  open: boolean;
  isMaximized: boolean;
  children?: React.ReactNode;
  leftOrRight: string;
  offset_x: number;
  offset_y: number;
}

/**
 * チャットパネルコンポーネント
 */
const ChatPanel: React.FC<ChatPanelProps> = ({
  open,
  isMaximized,
  children,
  leftOrRight,
  offset_x,
  offset_y,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * チャットパネルのスタイル
   */
  const themeDesign = getThemeDesign();
  const panelStyles = {
    ...getDockPositionStyles(
      leftOrRight,
      offset_x,
      offset_y,
      isMaximized,
      themeDesign.alignment,
      themeDesign.chat_margin_horizontal,
      themeDesign.chat_margin_vertical
    ),
    backgroundColor: "var(--main-bg)",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08)",
    zIndex: 200000, // ChatButtonより高くしてくさい
  };

  /**
   * チャットパネルのメッセージイベント
   */
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.action === "closeChatbot") {
        if (containerRef.current) containerRef.current.style.display = "none";
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const responsiveClasses = isMaximized
    ? "w-dvw h-dvh"
    : "w-full h-screen md:w-[var(--chat-width)] md:h-[var(--chat-height)]";

  const maximizedOverride = isMaximized
    ? {
        top: 0,
        bottom: 0,
        width: "100vw",
        height: "100vh",
        maxWidth: "100vw",
        maxHeight: "100vh",
        overflow: "hidden",
        ...(leftOrRight === 'right'
              ? { right: 0 }
              : { left: 0 }),
      }
    : {};

  return (
    <div
      ref={containerRef}
      className={`fixed transition-all duration-300 ease-out transform ${responsiveClasses} ${
        open
          ? "opacity-100 scale-100"
          : "opacity-0 scale-95 pointer-events-none"
      } ${isMaximized ? "rounded-none shadow-none" : "rounded-3xl shadow-lg"} ${
        leftOrRight === "right"
          ? "pathbee-transform-origin-bottom-right"
          : "pathbee-transform-origin-bottom-left"
      } overflow-hidden will-change-transform`}
      style={{
        ...panelStyles,
        ...maximizedOverride,
      }}
      role="dialog"
      aria-modal={isMaximized}
      aria-label="チャットパネル"
    >
      {children}
    </div>
  );
};

export default ChatPanel;
